import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const S = {
  burgundy: '#800020',
  burgundy2: '#722f37',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  borderLight: '#e2e8f0',
  bgCard: '#f8fafc',
};

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 10,
  fontSize: 13,
  color: S.textPrimary,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: S.textSecondary, marginBottom: 6 };

const useClaimNewFunctions = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState(null); // MongoDB _id — used for navigation
  const [createdClaimNumber, setCreatedClaimNumber] = useState(null); // human-readable number
  const [workflowMsg, setWorkflowMsg] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);

  const [formData, setFormData] = useState({
    claimantName: '',
    company: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    canton: '',
    specialization: '',
    policyNumber: '',
    lineOfBusiness: '',
    lossDate: '',
    priority: 'medium',
    reportedBy: 'claimant',
    reserveAmount: '',
    description: '',
  });

  const steps = [
    t('screens.claimsScreen.new.stepClaimant'),
    t('screens.claimsScreen.new.stepLossDetails'),
    t('screens.claimsScreen.new.stepDocuments'),
    t('screens.claimsScreen.new.stepReview'),
  ];

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleCreate = async () => {
    // VALIDATION
    if (!formData.claimantName?.trim()) {
      toast.error(t('screens.claimsScreen.new.validateClaimantRequired'));
      setStep(1);
      return;
    }
    if (!formData.email?.trim()) {
      toast.error(t('screens.claimsScreen.new.validateEmailRequired'));
      setStep(1);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(t('screens.claimsScreen.new.validateEmailInvalid'));
      setStep(1);
      return;
    }
    if (!formData.phone?.trim()) {
      toast.error(t('screens.claimsScreen.new.validatePhoneRequired'));
      setStep(1);
      return;
    }
    if (formData.phone.replace(/\D/g, '').length < 10) {
      toast.error(t('screens.claimsScreen.new.validatePhoneLength'));
      setStep(1);
      return;
    }
    if (!formData.policyNumber?.trim()) {
      toast.error(t('screens.claimsScreen.new.validatePolicyRequired'));
      setStep(1);
      return;
    }
    if (!formData.lineOfBusiness) {
      toast.error(t('screens.claimsScreen.new.validateLobRequired'));
      setStep(2);
      return;
    }
    if (!formData.lossDate) {
      toast.error(t('screens.claimsScreen.new.validateLossDateRequired'));
      setStep(2);
      return;
    }
    if (!formData.description?.trim()) {
      toast.error(t('screens.claimsScreen.new.validateDescriptionRequired'));
      setStep(2);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        claimant: {
          name: formData.claimantName.trim(),
          company: formData.company?.trim() || '',
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          // Optional analytics fields — only include if provided so we don't
          // overwrite existing claimant data with empty strings on edit-style flows.
          ...(formData.dateOfBirth    && { dateOfBirth:    formData.dateOfBirth }),
          ...(formData.canton         && { canton:         formData.canton.trim() }),
          ...(formData.specialization && { specialization: formData.specialization.trim() }),
        },
        policyNumber: formData.policyNumber.trim(),
        lineOfBusiness: formData.lineOfBusiness,
        lossDate: formData.lossDate,
        priority: formData.priority || 'medium',
        reportedBy: formData.reportedBy || 'claimant',
        description: formData.description.trim(),
        ...(formData.reserveAmount !== '' && formData.reserveAmount !== null && formData.reserveAmount !== undefined && {
          reserveAmount: Number(formData.reserveAmount),
        }),
      };
      const resp = await axios.post(`${BASE_URL}/claims`, payload, { withCredentials: true });
      const created = resp.data?.data;
      // Use _id for navigation (backend lookups by _id), claimNumber for display
      setCreatedId(created?._id);
      setCreatedClaimNumber(created?.claimNumber || created?._id);
      if (created?.assignedTo?.name) {
        setWorkflowMsg(t('screens.claimsScreen.new.workflowAutoAssigned', { name: created.assignedTo.name }));
      }
      // Upload any pending documents selected in Step3
      if (pendingFiles.length > 0 && created?._id) {
        for (const file of pendingFiles) {
          const fd = new FormData();
          fd.append('file', file);
          try {
            await axios.post(`${BASE_URL}/claims/${created._id}/upload`, fd, { withCredentials: true });
          } catch {
            // Non-fatal — claim created; documents can be uploaded from claim detail
          }
        }
      }
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('screens.claimsScreen.new.createClaimFailed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    S,
    inputStyle,
    labelStyle,
    history,
    step,
    setStep,
    submitted,
    setSubmitted,
    submitting,
    setSubmitting,
    createdId,
    setCreatedId,
    createdClaimNumber,
    setCreatedClaimNumber,
    workflowMsg,
    setWorkflowMsg,
    pendingFiles,
    setPendingFiles,
    formData,
    setFormData,
    steps,
    handleChange,
    handleCreate,
  };
};

export default useClaimNewFunctions;
